import { ChangeEvent, useEffect, useReducer, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SelectChangeEvent, styled } from '@mui/material';
import { toast } from 'react-toastify';
import { unwrapResult } from '@reduxjs/toolkit';

import { CustomAlert, CustomButton, CustomInput, CustomSelect } from '../../../../../../../shared/uiComponents';
import { errorNormalizer, validateNumber } from '../../../../../../../shared/Helpers/functions';
import { BACKGROUND } from '../../../../../../../shared/Helpers/colors';
import { initialState } from './initialState';
import { ACTIONS, TargetReducer } from './reducer';

import { DispatchProperties, useSelector } from '../../../../../../../redux/store';
import {
	createTarget,
	getMasteryCriteria,
	getTargets,
} from '../../../../../../../redux/State/clientSlice/programTargetSlice';
import {
	CreateProgramTargetProperties,
	TargetTypes,
} from '../../../../../../../redux/API/ClientAPIHelpers/programTargetsProperties';

const CriteriaWrapper = styled('div')(() => ({
	display: 'flex',
	gap: '16px',
	alignItems: 'center',
	flexWrap: 'wrap',
	'>div': {
		flex: '1 0 0',
	},
}));

const CreateTargetContent = ({ onClose, programId }: { onClose: () => void; programId: string }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const includeMastered = useSelector((state) => state.programTarget.includeMastered);

	const loading = useSelector((state) => state.programTarget.loading);
	const masteryOptions = useSelector((state) => state.programTarget.masteryCriterias);

	const [targetData, setTargetData] = useReducer(TargetReducer, initialState);
	const {
		name,
		// goalName,
		sd,
		targetInstructions,
		typeId,
		errorCorrection,
		reinforcement,
		masteryCriteria,
		consecutiveDays,
		responsePercentage,
	} = targetData;

	const [stepsValue, setStepsValue] = useState<string>('');
	const [error, setError] = useState<string>('');

	useEffect(() => {
		dispatch(getMasteryCriteria());
	}, [dispatch]);

	const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setTargetData({ type: ACTIONS.setName, payload: value });
	};

	// const onGoalNameChange = (event: ChangeEvent<HTMLInputElement>) => {
	//   const { value } = event.target;
	//   setTargetData({ type: ACTIONS.setGoalName, payload: value });
	// };

	const onSdChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setTargetData({ type: ACTIONS.setSD, payload: value });
	};

	const onTargetInstructionsChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setTargetData({ type: ACTIONS.setTargetInstructions, payload: value });
	};

	const onErrorCorrectionChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setTargetData({ type: ACTIONS.setErrorCorrection, payload: value });
	};

	const onReinforcementChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setTargetData({ type: ACTIONS.setReinforcement, payload: value });
	};

	const onTypeIdChange = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setTargetData({ type: ACTIONS.setTypeId, payload: value });
	};

	const onMasteryCriteriaChange = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		const payload = parseInt(value);
		setTargetData({ type: ACTIONS.setMasteryCriteria, payload });
	};

	const onConsecutiveDaysChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		if (!validateNumber(value)) return;
		setTargetData({
			type: ACTIONS.setConsecutiveDays,
			payload: value,
		});
	};

	const onResponsePercentageChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		if (!validateNumber(value)) return;
		setTargetData({
			type: ACTIONS.setResponsePercentage,
			payload: value,
		});
	};

	const onStepsChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setStepsValue(value);
	};

	const saveTargetHandler = () => {
		if (!programId) return;

		let data: CreateProgramTargetProperties = { ...targetData };

		if (stepsValue) {
			const steps = stepsValue.split(',').map((s) => ({ name: s.trim() }));
			data = { ...data, steps };
		}

		dispatch(createTarget({ data, programId }))
			.then(unwrapResult)
			.then(() => {
				toast('Created');
				dispatch(getTargets({ programId, includeMastered }));
				onClose();
			})
			.catch(errorNormalizer);
	};

	useEffect(() => {
		if (name) {
			setError('');

			return;
		}

		setError('Required');
	}, [name]);

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				gap: '24px',
				padding: '24px',
				borderRadius: '14px',
				background: BACKGROUND.PRIMARY[100],
				boxShadow: `0 1px 2px 0 rgba(0, 0, 0, 0.04)`,
			}}
		>
			<CustomInput
				label="Target Name"
				className="marginBottom16"
				value={name}
				onChange={onNameChange}
				error={!name}
				errorMessage={error}
				backgroundType="base"
			/>
			<CustomInput label="SD" className="marginBottom16" value={sd} onChange={onSdChange} backgroundType="base" />
			<CriteriaWrapper>
				<CustomSelect
					label="Mastery Criteria"
					className="marginBottom16"
					value={masteryCriteria.toString()}
					setValue={onMasteryCriteriaChange}
					data={masteryOptions}
				/>
				<CustomInput
					label="Consecutive Days"
					className="marginBottom16"
					value={consecutiveDays}
					onChange={onConsecutiveDaysChange}
					error={!consecutiveDays}
					backgroundType="base"
				/>
				<CustomInput
					label="Response Percentage"
					className="marginBottom16"
					value={responsePercentage}
					onChange={onResponsePercentageChange}
					error={!responsePercentage}
					backgroundType="base"
				/>
			</CriteriaWrapper>
			{/* <CustomInput
        label="Goal Name (for reporting)"
        className="marginBottom16"
        value={goalName}
        onChange={onGoalNameChange}
        multiline={true}
        backgroundType="base"
      /> */}
			<CustomInput
				label="Target Instructions"
				className="marginBottom16"
				value={targetInstructions}
				onChange={onTargetInstructionsChange}
				multiline
				backgroundType="base"
			/>
			<CustomInput
				label="Error Correction"
				className="marginBottom16"
				value={errorCorrection}
				onChange={onErrorCorrectionChange}
				multiline
				backgroundType="base"
			/>
			<CustomInput
				label="Reinforcement"
				className="marginBottom16"
				value={reinforcement}
				onChange={onReinforcementChange}
				multiline
				backgroundType="base"
			/>
			<CustomSelect
				label="Target type"
				className="marginBottom16"
				value={typeId.toString()}
				setValue={onTypeIdChange}
				data={[
					{ id: TargetTypes.DTT, name: 'DTT' },
					{ id: TargetTypes.TA, name: 'Task Analysis' },
				]}
			/>
			{typeId === TargetTypes.TA && (
				<CustomInput
					label="Steps"
					className="marginBottom16"
					value={stepsValue}
					onChange={onStepsChange}
					multiline
					description="Separate with comma"
					backgroundType="base"
				/>
			)}
			<CustomButton
				title="Save"
				onClick={saveTargetHandler}
				loading={loading}
				disabled={loading || !name || !consecutiveDays || !responsePercentage}
			/>
		</div>
	);
};

const CreateTarget = ({ programId, mobileVersion }: { mobileVersion?: boolean; programId: string }) => {
	const [open, setOpen] = useState<boolean>(false);

	const openHandler = () => setOpen(true);
	const closeHandler = () => setOpen(false);

	return (
		<>
			<CustomButton title="Create New Target" onClick={openHandler} className={mobileVersion ? '' : 'width150'} />
			<CustomAlert
				open={open}
				onClose={closeHandler}
				title="Target Information"
				Content={({ onClose }) => <CreateTargetContent onClose={onClose} programId={programId} />}
				maxWidth="md"
				isSecondary
			/>
		</>
	);
};

export default CreateTarget;
